import { create } from 'zustand';
import { api } from './api';

/**
 * Денежные правила витрины: пороги подарков, бесплатной доставки и коинов.
 *
 * ЗАЧЕМ ЭТОТ ФАЙЛ. До него порог «1200 ₽ на подарок за первый заказ» жил на
 * сайте в трёх местах, пороги статусных роллов — в двух, бесплатная доставка —
 * в трёх, и все они дублировали числа из бэкенда. Одно расхождение — и человек
 * видит одну сумму, а списывается другая; это уже попадало в лог ошибок как
 * «total с фронта != серверный».
 *
 * ГЛАВНОЕ: ЭТО ТОЛЬКО ОТОБРАЖЕНИЕ.
 * Авторитетный расчёт заказа живёт на сервере (azia_back → routers/orders.js).
 * Здешние значения нужны, чтобы показать прогресс до подарка, надпись «до
 * бесплатной доставки осталось…» и лимит коинов. Если витрина отстанет от
 * сервера на минуту кэша, сервер всё равно посчитает правильно — но именно
 * поэтому значения берутся С СЕРВЕРА, а не переписываются сюда руками.
 *
 * ДО ЗАГРУЗКИ и при недоступном API действуют DEFAULT_MONEY_RULES — ровно те
 * числа, что зашиты в бэкенде (utils/moneyRules.js). Ноль или пустота
 * недопустимы: ноль в пороге бесплатной доставки означал бы «доставка всем
 * бесплатно», а ноль в пороге подарка — подарок к каждому заказу.
 */

// ─── Типы ответа GET /api/money-rules ────────────────────────────────────────

export type StatusRollType = 'silver' | 'gold' | 'platinum';

export interface StatusRollRule {
  type: StatusRollType;
  label: string;
  product_id: number;
  min_sum: number;
}

export interface LoyaltyLevelRule {
  level: number;
  name: string;
  min_total: number;
  earn_rate: number;
}

export interface MoneyRules {
  /** 'db' — значения из админки; 'defaults' — миграция ещё не применена. */
  source: 'db' | 'defaults';
  first_order: {
    min_sum: number;
    product_id: number;
  };
  /** По убыванию порога — в том же порядке, в каком их проверяет сервер. */
  status_rolls: StatusRollRule[];
  free_delivery: {
    min_sum: number;
    max_zone_cost: number;
  };
  loyalty: {
    max_spend_percent: number;
    min_cart_for_spend: number;
    coin_expiry_months: number;
    /**
     * От чего сервер считает порог и лимит списания коинов.
     * 'items_plus_delivery' — вместе с доставкой (решение владельца по
     * вопросу 3: «сервер прав»). Поле отдаётся сервером явно, чтобы витрина
     * не воспроизводила формулу по памяти.
     */
    spend_base: 'items_plus_delivery' | 'items_only';
    levels: LoyaltyLevelRule[];
  };
}

// ─── Значения по умолчанию ───────────────────────────────────────────────────
//
// Копия зашитых значений бэкенда (azia_back/utils/moneyRules.js →
// DEFAULT_THRESHOLDS / DEFAULT_GIFT_PRODUCTS / DEFAULT_LOYALTY_LEVELS).
// Живут здесь ради первого кадра и офлайна, а не как «настройка сайта»:
// менять их вручную нельзя — правка делается в админке.
export const DEFAULT_MONEY_RULES: MoneyRules = {
  source: 'defaults',
  first_order: { min_sum: 1200, product_id: 77 },
  status_rolls: [
    { type: 'platinum', label: 'Platinum-ролл', product_id: 79, min_sum: 4500 },
    { type: 'gold', label: 'Gold-ролл', product_id: 78, min_sum: 3500 },
    { type: 'silver', label: 'Silver-ролл', product_id: 76, min_sum: 2500 },
  ],
  free_delivery: { min_sum: 3000, max_zone_cost: 265 },
  loyalty: {
    max_spend_percent: 15,
    min_cart_for_spend: 1500,
    coin_expiry_months: 2,
    spend_base: 'items_plus_delivery',
    levels: [
      { level: 1, name: 'Bronze', min_total: 0, earn_rate: 1 },
      { level: 2, name: 'Silver', min_total: 7000, earn_rate: 3 },
      { level: 3, name: 'Gold', min_total: 15000, earn_rate: 5 },
      { level: 4, name: 'Platinum', min_total: 30000, earn_rate: 7 },
    ],
  },
};

// ─── Разбор ответа ───────────────────────────────────────────────────────────
//
// Ответ приходит из сети, поэтому проверяется, а не приводится типом.
// Любое непонятное или неположительное значение заменяется прежним: показать
// «подарок от 0 ₽» страшнее, чем показать вчерашний порог.

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function num(value: unknown, fallback: number, { allowZero = true } = {}): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  if (parsed < 0) return fallback;
  if (!allowZero && parsed === 0) return fallback;
  return parsed;
}

function parseStatusRolls(raw: unknown): StatusRollRule[] {
  if (!Array.isArray(raw)) return DEFAULT_MONEY_RULES.status_rolls;

  const byType = new Map<StatusRollType, StatusRollRule>();
  for (const entry of raw) {
    const item = asRecord(entry);
    const type = item.type;
    if (type !== 'silver' && type !== 'gold' && type !== 'platinum') continue;
    const fallback = DEFAULT_MONEY_RULES.status_rolls.find((r) => r.type === type);
    if (!fallback) continue;
    byType.set(type, {
      type,
      label: typeof item.label === 'string' && item.label ? item.label : fallback.label,
      product_id: num(item.product_id, fallback.product_id, { allowZero: false }),
      min_sum: num(item.min_sum, fallback.min_sum, { allowZero: false }),
    });
  }

  const rolls = DEFAULT_MONEY_RULES.status_rolls.map(
    (fallback) => byType.get(fallback.type) ?? fallback
  );
  // Порядок задаём сами: логика «самый ценный доступный ролл» опирается на
  // убывание порога, а не на порядок, в котором их прислал сервер.
  return rolls.sort((a, b) => b.min_sum - a.min_sum);
}

export function parseMoneyRules(raw: unknown): MoneyRules {
  const body = asRecord(raw);
  const firstOrder = asRecord(body.first_order);
  const freeDelivery = asRecord(body.free_delivery);
  const loyalty = asRecord(body.loyalty);
  const defaults = DEFAULT_MONEY_RULES;

  const levels = Array.isArray(loyalty.levels)
    ? loyalty.levels
        .map((entry) => {
          const level = asRecord(entry);
          return {
            level: num(level.level, 0),
            name: typeof level.name === 'string' ? level.name : '',
            min_total: num(level.min_total, 0),
            earn_rate: num(level.earn_rate, 0),
          };
        })
        .filter((level) => level.level > 0 && level.name !== '')
    : [];

  return {
    source: body.source === 'db' ? 'db' : 'defaults',
    first_order: {
      // allowZero: false — ноль здесь означал бы подарок к любому заказу.
      min_sum: num(firstOrder.min_sum, defaults.first_order.min_sum, { allowZero: false }),
      product_id: num(firstOrder.product_id, defaults.first_order.product_id, { allowZero: false }),
    },
    status_rolls: parseStatusRolls(body.status_rolls),
    free_delivery: {
      min_sum: num(freeDelivery.min_sum, defaults.free_delivery.min_sum, { allowZero: false }),
      // А здесь ноль законен: он выключает акцию бесплатной доставки.
      max_zone_cost: num(freeDelivery.max_zone_cost, defaults.free_delivery.max_zone_cost),
    },
    loyalty: {
      max_spend_percent: num(loyalty.max_spend_percent, defaults.loyalty.max_spend_percent),
      min_cart_for_spend: num(loyalty.min_cart_for_spend, defaults.loyalty.min_cart_for_spend),
      coin_expiry_months: num(loyalty.coin_expiry_months, defaults.loyalty.coin_expiry_months, {
        allowZero: false,
      }),
      spend_base: loyalty.spend_base === 'items_only' ? 'items_only' : 'items_plus_delivery',
      levels: levels.length > 0 ? levels : defaults.loyalty.levels,
    },
  };
}

// ─── Хранилище ───────────────────────────────────────────────────────────────

interface MoneyRulesState {
  rules: MoneyRules;
  /** true после первой удачной загрузки — до неё показываются значения по умолчанию. */
  loaded: boolean;
  setRules: (rules: MoneyRules) => void;
}

export const useMoneyRulesStore = create<MoneyRulesState>()((set) => ({
  rules: DEFAULT_MONEY_RULES,
  loaded: false,
  setRules: (rules) => set({ rules, loaded: true }),
}));

/**
 * Правила вне React (сторы, обработчики, подписки zustand).
 * Компонентам нужен хук `useMoneyRules()` — иначе они не перерисуются, когда
 * значения приедут с сервера, и покажут вчерашний порог рядом со свежей суммой.
 */
export function getMoneyRules(): MoneyRules {
  return useMoneyRulesStore.getState().rules;
}

/** Правила в компоненте. Перерисовывает при обновлении с сервера. */
export function useMoneyRules(): MoneyRules {
  return useMoneyRulesStore((state) => state.rules);
}

// ─── Загрузка ────────────────────────────────────────────────────────────────

let inFlight: Promise<MoneyRules> | null = null;

/**
 * Загрузить правила с сервера. Вызывается один раз при старте приложения
 * (см. providers/money-rules-init.tsx).
 *
 * Ошибка сети не пробрасывается: витрина обязана открыться и на значениях по
 * умолчанию. Одновременные вызовы разделяют один запрос.
 */
export async function fetchMoneyRules(): Promise<MoneyRules> {
  if (inFlight) return inFlight;

  inFlight = api
    .get<unknown>('/money-rules')
    .then((response) => {
      const rules = parseMoneyRules(response.data);
      useMoneyRulesStore.getState().setRules(rules);
      return rules;
    })
    .catch(() => {
      // Тихо: пороги — не то, из-за чего человеку показывают ошибку. Витрина
      // продолжает работать на значениях по умолчанию, а сервер при оформлении
      // всё равно посчитает по своим.
      return DEFAULT_MONEY_RULES;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

// ─── Производные значения ────────────────────────────────────────────────────

/**
 * Самый ценный статусный ролл, доступный на эту сумму.
 * Сумма считается без доставки, соусов и категории «Роллы -30%» — так же, как
 * её считает сервер (statusEligiblePrice в orders.js).
 */
export function getAvailableStatusRollRule(
  eligiblePrice: number,
  rules: MoneyRules = getMoneyRules()
): StatusRollRule | null {
  return rules.status_rolls.find((roll) => eligiblePrice >= roll.min_sum) ?? null;
}

/**
 * Правило конкретного статусного ролла (порог + товар).
 *
 * Всегда возвращает правило: если сервер по какой-то причине не прислал этот
 * тир, берётся значение по умолчанию. Витрине нужен порог, а не `undefined` —
 * иначе на экране появится «от 0 ₽» или пустое место.
 */
export function getStatusRollRule(
  type: StatusRollType,
  rules: MoneyRules = getMoneyRules()
): StatusRollRule {
  const fromRules = rules.status_rolls.find((roll) => roll.type === type);
  if (fromRules) return fromRules;
  const fallback = DEFAULT_MONEY_RULES.status_rolls.find((roll) => roll.type === type);
  // Тип StatusRollType закрыт тремя значениями, и все три есть в умолчаниях,
  // поэтому сюда попасть нельзя — но возвращать undefined наружу мы не хотим.
  return fallback ?? DEFAULT_MONEY_RULES.status_rolls[0];
}

/**
 * Действует ли акция бесплатной доставки.
 *
 * Повторяет условие сервера дословно (orders.js): доставка платная, её
 * стоимость не выше порога зоны, сумма товаров не ниже порога суммы.
 */
export function isFreeDeliveryPromoActive(
  deliveryCost: number,
  itemsTotal: number,
  rules: MoneyRules = getMoneyRules()
): boolean {
  return (
    deliveryCost > 0 &&
    deliveryCost <= rules.free_delivery.max_zone_cost &&
    itemsTotal >= rules.free_delivery.min_sum
  );
}
